import React, { useContext, useMemo, useState } from "react";
import { Controller } from "react-hook-form";
import {
  TextField,
  Typography,
  MenuItem,
  Box,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  RadioGroup,
  FormControlLabel,
  Radio,
  Tooltip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import SingleSelectSearch from "@/modules/common/components/input/single-autocomplete-search";
import MoneyInput from "@/modules/common/components/input/money-input";
import usePromiseSaleForm from "@/modules/lead-page/hooks/lead-info/status/forms/promise-sale-form.hook";
import useGetLeadPropertyTracks, {
  LeadPropertyTrackOption,
} from "@/modules/lead-page/hooks/get-lead-property-tracks";
import FormFooter from "@/modules/lead-page/components/lead-info/edit-fields/status/forms/form-footer";
import { FileUploadButton } from "@/modules/common/components/input/file-upload/file-upload";
import {
  StaticFormProps,
  CommissionTypes,
  UnitIdTypes,
} from "@/modules/lead-page/types";
import { LocationDataParams, Options } from "@/modules/common/types";
import useLocationsData from "@/modules/common/hooks/locations-data.hook";
import { LocationsResponse } from "@/modules/common/dtos/index.v2";
import ReadOnlyTextInput from "@/modules/common/components/input/readonly-text-input";
import { sale_categories } from "@/modules/common/constants";
import Grid from "@mui/material/GridLegacy";
import { SalesStatuses } from "@/modules/sales/constants";
import { isSameStatus } from "@/modules/lead-page/utils/status-name";
import { LeadPageContext } from "@/modules/lead-page/context/lead-page.context";

const PromiseSaleForm: React.FC<StaticFormProps> = ({
  handleSaleSubmission,
  saleType,
  handleClose,
  sale,
  handleDeleteFile,
  handleFiles,
  files,
  isLoadingSubmition,
  statusName,
}) => {
  const [selectedUnitLocation, setSelectedUnitLocation] = useState<Options>();

  const { lead } = useContext(LeadPageContext);
  const leadTypeName = lead?.data?.lead_type?.lead_type || "";
  // Property Track belongs to resale sellers only — hide it for resale buyers.
  const isResaleSeller = leadTypeName.toLowerCase().includes("seller");

  const {
    errors,
    control,
    onSubmit,
    handleSubmit,
    selectedCompound,
    propertyTypeOptions,
    nawyCommissionType,
    setValue,
    watch,
  } = usePromiseSaleForm({
    selectedUnitLocation,
    handleSaleSubmission,
    existingSale: sale,
    statusName,
  });

  const {
    options: leadTrackOptions,
    isLoading: isLoadingLeadTracks,
  } = useGetLeadPropertyTracks();

  const selectedPropertyTrackUId = watch("propertyTrackUId");

  // Preserve a previously-saved track that no longer maps to the current tracks list (edit mode).
  const trackDropdownOptions = useMemo<LeadPropertyTrackOption[]>(() => {
    const existingTrackUId = sale?.propertyTrackUid ?? "";
    const existingUnitNumber = sale?.unitNumber ?? "";

    if (!existingTrackUId && !existingUnitNumber) {
      return leadTrackOptions;
    }

    const matchesExisting = leadTrackOptions.some((opt) =>
      existingTrackUId
        ? opt.trackUId === existingTrackUId
        : opt.unitNumber === existingUnitNumber,
    );

    if (matchesExisting) {
      return leadTrackOptions;
    }

    const preservedTrackUId = existingTrackUId || `preserved-${existingUnitNumber}`;
    const compoundName = sale?.compound?.name ?? "";
    const labelParts = [compoundName, existingUnitNumber].filter(Boolean);
    const baseLabel = labelParts.join(" - ");
    const preservedOption: LeadPropertyTrackOption = {
      trackUId: preservedTrackUId,
      unitNumber: existingUnitNumber,
      compoundName,
      label: `${baseLabel || existingUnitNumber} (existing)`,
    };
    return [...leadTrackOptions, preservedOption];
  }, [
    leadTrackOptions,
    sale?.propertyTrackUid,
    sale?.unitNumber,
    sale?.compound?.name,
  ]);

  // Auto-select when exactly one track is available and nothing is selected yet.
  React.useEffect(() => {
    if (
      !isLoadingLeadTracks &&
      leadTrackOptions.length === 1 &&
      !selectedPropertyTrackUId
    ) {
      const onlyOption = leadTrackOptions[0];
      if (!onlyOption.unitNumber) {
        return;
      }
      setValue("propertyTrackUId", onlyOption.trackUId, {
        shouldValidate: true,
      });
      setValue("unitNumber", onlyOption.unitNumber, {
        shouldValidate: true,
      });
    }
  }, [
    isLoadingLeadTracks,
    leadTrackOptions,
    selectedPropertyTrackUId,
    setValue,
  ]);

  // In edit mode, pre-seed the dropdown selection from the existing sale's track / unit number.
  React.useEffect(() => {
    if (selectedPropertyTrackUId) {
      return;
    }
    const existingTrackUId = sale?.propertyTrackUid ?? "";
    const existingUnitNumber = sale?.unitNumber ?? "";

    if (!existingTrackUId && !existingUnitNumber) {
      return;
    }

    const matchingOption = trackDropdownOptions.find((opt) =>
      existingTrackUId
        ? opt.trackUId === existingTrackUId
        : opt.unitNumber === existingUnitNumber,
    );

    if (matchingOption) {
      setValue("propertyTrackUId", matchingOption.trackUId, {
        shouldValidate: true,
      });
      setValue("unitNumber", matchingOption.unitNumber, {
        shouldValidate: true,
      });
    }
  }, [
    sale?.propertyTrackUid,
    sale?.unitNumber,
    selectedPropertyTrackUId,
    trackDropdownOptions,
    setValue,
  ]);

  const {
    locationsData: compoundsData,
    locationsLoading: compoundsLoading,
    locationSearchTerm: compoundsSearchTerm,
    handleSearchChange: handleCompoundSearchChange,
  } = useLocationsData({
    locationDataParams: LocationDataParams.COMPOUND,
  });

  const compoundOptions = useMemo(() => {
    const mapCompoundToOption = (
      location: LocationsResponse["data"]["results"][number]
    ) => ({
      value: location?.compound?.uid,
      optionValueUid: location?.compound?.area?.uid,
      label: `${location?.compound?.name} - ${location?.compound?.area?.name}`,
      developerName: location?.compound?.developer?.name ?? "",
    });

    const baseOptions = (compoundsData?.results || []).map(mapCompoundToOption);

    const saleCompoundOption = sale?.compound
      ? {
          value: sale.compound.uid,
          optionValueUid: sale.area?.uid,
          label: `${sale.compound.name}${
            sale.area?.name ? ` - ${sale.area.name}` : ""
          }`,
          developerName: sale.compound.developer?.name ?? "",
        }
      : null;

    return saleCompoundOption &&
      !baseOptions.find((opt) => opt.value === saleCompoundOption.value)
      ? [...baseOptions, saleCompoundOption]
      : baseOptions;
  }, [compoundsData?.results, sale?.compound, sale?.area]);

  React.useEffect(() => {
    if (sale?.compound && !selectedUnitLocation) {
      const saleOption = {
        value: sale.compound.uid,
        optionValueUid: sale.area?.uid,
        label: `${sale.compound.name}${
          sale.area?.name ? ` - ${sale.area.name}` : ""
        }`,
        developerName: sale.compound.developer?.name ?? "",
      };
      setSelectedUnitLocation(saleOption);
    }
  }, [sale, selectedUnitLocation]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Typography variant="h3_med">
        {sale ? "Edit" : "New"} {saleType} Form
      </Typography>

      <Grid container spacing={2}>
        <Grid item mobile={12} desktop={6}>
          <Controller
            name="promiseSaleDate"
            control={control}
            render={({ field }) => (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Promise Sale Date"
                  value={field.value}
                  onChange={(newValue: Date | null) => {
                    const adjustedDate = newValue
                      ? new Date(newValue.setHours(12, 0, 0, 0))
                      : null;
                    field.onChange(adjustedDate);
                  }}
                  format="dd/MM/yyyy"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.promiseSaleDate,
                      helperText: errors.promiseSaleDate?.message,
                    },
                  }}
                />
              </LocalizationProvider>
            )}
          />
        </Grid>

        <Grid item mobile={12} desktop={6}>
          <SingleSelectSearch
            value={selectedCompound || sale?.compound?.uid}
            defaultValue={selectedCompound || sale?.compound?.uid}
            size="medium"
            options={compoundOptions}
            isLoading={compoundsLoading}
            searchValue={compoundsSearchTerm}
            onSearchValueChange={handleCompoundSearchChange}
            name="compoundUId"
            control={control}
            label="Compound"
            error={errors.compoundUId?.message}
            onValueChange={(value) => {
              const selectedOption = compoundOptions.find(
                (opt) => opt.value === value
              );
              setSelectedUnitLocation(selectedOption);
            }}
          />
        </Grid>

        <Grid item mobile={12} desktop={6}>
          <ReadOnlyTextInput
            label="Developer"
            value={
              selectedUnitLocation?.developerName || sale?.developer?.name || ""
            }
          />
        </Grid>

        <Grid item mobile={12} desktop={6}>
          <Controller
            name="unitValue"
            control={control}
            render={({ field }) => (
              <MoneyInput
                label="Expected Unit Value (EGP)"
                value={field.value || sale?.unitValue}
                onChange={(e) => field.onChange(e.target.value)}
                isError={!!errors.unitValue}
                helperText={errors.unitValue?.message}
              />
            )}
          />
        </Grid>

        {isResaleSeller && (
        <Grid item mobile={12} desktop={6}>
          <Controller
            name="propertyTrackUId"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.propertyTrackUId}>
                <InputLabel id="property-track-label">Property Track</InputLabel>
                <Select
                  {...field}
                  labelId="property-track-label"
                  id="property-track"
                  label="Property Track"
                  value={field.value || ""}
                  disabled={isLoadingLeadTracks}
                  onChange={(event) => {
                    const trackUId = event.target.value as string;
                    field.onChange(trackUId);
                    const matched = trackDropdownOptions.find(
                      (opt) => opt.trackUId === trackUId,
                    );
                    if (matched) {
                      setValue("unitNumber", matched.unitNumber, {
                        shouldValidate: true,
                      });
                    }
                  }}
                >
                  {trackDropdownOptions.length === 0 && (
                    <MenuItem value="" disabled>
                      <em>No property tracks linked to this lead</em>
                    </MenuItem>
                  )}
                  {trackDropdownOptions.map((option) => (
                    <MenuItem key={option.trackUId} value={option.trackUId}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.propertyTrackUId && (
                  <FormHelperText>
                    {errors.propertyTrackUId.message}
                  </FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Grid>
        )}

        <Grid item mobile={12} desktop={6}>
          <Controller
            name="unitNumber"
            control={control}
            render={({ field }) =>
              isResaleSeller ? (
                <FormControl fullWidth error={!!errors.unitNumber}>
                  <InputLabel id="unit-number-label">Unit Number</InputLabel>
                  <Select
                    {...field}
                    labelId="unit-number-label"
                    id="unit-number"
                    label="Unit Number"
                    value={field.value || ""}
                    disabled={isLoadingLeadTracks}
                    onChange={(event) => {
                      const unitNumber = event.target.value as string;
                      field.onChange(unitNumber);
                      const matched = trackDropdownOptions.find(
                        (opt) => opt.unitNumber === unitNumber,
                      );
                      if (matched) {
                        setValue("propertyTrackUId", matched.trackUId, {
                          shouldValidate: true,
                        });
                      }
                    }}
                  >
                    {trackDropdownOptions.length === 0 && (
                      <MenuItem value="" disabled>
                        <em>No units linked to this lead</em>
                      </MenuItem>
                    )}
                    {trackDropdownOptions.map((option) => {
                      const hasUnitNumber = Boolean(option.unitNumber);
                      if (!hasUnitNumber) {
                        return (
                          <Tooltip
                            key={`unit-${option.trackUId}`}
                            title="This property is missing a unit number. Open the property details and add a unit number before linking it to a sale."
                            placement="right"
                          >
                            <span>
                              <MenuItem value="" disabled>
                                <em>
                                  {option.compoundName || "Unit"} - no unit
                                  number
                                </em>
                              </MenuItem>
                            </span>
                          </Tooltip>
                        );
                      }
                      return (
                        <MenuItem
                          key={`unit-${option.trackUId}`}
                          value={option.unitNumber}
                        >
                          {option.unitNumber}
                        </MenuItem>
                      );
                    })}
                  </Select>
                  {errors.unitNumber && (
                    <FormHelperText>{errors.unitNumber.message}</FormHelperText>
                  )}
                </FormControl>
              ) : (
                <TextField
                  {...field}
                  label="Unit Number"
                  fullWidth
                  value={field.value || ""}
                  error={!!errors.unitNumber}
                  helperText={errors.unitNumber?.message}
                />
              )
            }
          />
        </Grid>

        <Grid item mobile={12} desktop={6}>
          <Controller
            name="unitIdType"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.unitIdType}>
                <InputLabel id="unit-id-type-label">Unit Source</InputLabel>
                <Select
                  {...field}
                  labelId="unit-id-type-label"
                  id="unit-id-type"
                  label="Unit Source"
                  value={field.value || ""}
                >
                  <MenuItem value={UnitIdTypes.Nawy}>Nawy</MenuItem>
                  <MenuItem value={UnitIdTypes.Outside}>Outside</MenuItem>
                </Select>
                {errors.unitIdType && (
                  <FormHelperText>{errors.unitIdType.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Grid>

        <Grid item mobile={12} desktop={6}>
          <Controller
            name="nawyUnitId"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Nawy Unit ID"
                fullWidth
                error={!!errors.nawyUnitId}
                helperText={errors.nawyUnitId?.message}
              />
            )}
          />
        </Grid>

        <Grid item mobile={12} desktop={6}>
          <Controller
            name="propertyTypeUId"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.propertyTypeUId}>
                <InputLabel id="property-type-label">Property Type</InputLabel>
                <Select
                  {...field}
                  labelId="property-type-label"
                  id="property-type"
                  label="Property Type"
                  value={field.value || ""}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {propertyTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.propertyTypeUId && (
                  <FormHelperText>
                    {errors.propertyTypeUId.message}
                  </FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Grid>

        <Grid item mobile={12} desktop={6}>
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Nawy's Commission Type
            </Typography>
            <Controller
              name="nawyCommissionType"
              control={control}
              render={({ field }) => (
                <RadioGroup row {...field} value={field.value || ""}>
                  <FormControlLabel
                    value={CommissionTypes.Percentage}
                    control={<Radio />}
                    label="Percentage (%)"
                  />
                  <FormControlLabel
                    value={CommissionTypes.Fixed}
                    control={<Radio />}
                    label="Fixed Amount"
                  />
                </RadioGroup>
              )}
            />
            {errors.nawyCommissionType && (
              <FormHelperText error>
                {errors.nawyCommissionType.message}
              </FormHelperText>
            )}
          </Box>
        </Grid>

        <Grid item mobile={12} desktop={6}>
          <Controller
            name="nawyCommissionValue"
            control={control}
            render={({ field }) =>
              nawyCommissionType === CommissionTypes.Percentage ? (
                <TextField
                  {...field}
                  label="Commission Percentage"
                  type="number"
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                  InputProps={{
                    endAdornment: "%",
                  }}
                  InputLabelProps={{
                    shrink:
                      field.value || sale?.nawyCommissionValue ? true : false,
                  }}
                  value={field.value || sale?.nawyCommissionValue || ""}
                  fullWidth
                  disabled={!nawyCommissionType}
                  placeholder={
                    !nawyCommissionType ? "Select commission type first" : ""
                  }
                  error={!!errors.nawyCommissionValue}
                  helperText={errors.nawyCommissionValue?.message}
                />
              ) : nawyCommissionType === CommissionTypes.Fixed ? (
                <MoneyInput
                  label="Commission Amount (EGP)"
                  value={field.value || sale?.nawyCommissionValue}
                  onChange={(e) => field.onChange(e.target.value)}
                  disabled={!nawyCommissionType}
                  isError={!!errors.nawyCommissionValue}
                  helperText={errors.nawyCommissionValue?.message}
                />
              ) : (
                <TextField
                  label="Commission Value"
                  fullWidth
                  disabled
                  placeholder="Select commission type first"
                  helperText="Please select a commission type first"
                />
              )
            }
          />
        </Grid>

        <Grid item mobile={12} desktop={6}>
          <Controller
            name="commissionCollectedValue"
            control={control}
            render={({ field }) => (
              <MoneyInput
                label="Commission Collected Amount (EGP)"
                value={field.value || sale?.commissionCollectedValue}
                onChange={(e) => field.onChange(e.target.value)}
                isError={!!errors.commissionCollectedValue}
                helperText={errors.commissionCollectedValue?.message}
              />
            )}
          />
        </Grid>

        {!isSameStatus(statusName, SalesStatuses.CONGRATS_ITS_A_SALE) && (
          <Grid item mobile={12} desktop={6}>
            <Controller
              name="expectedContractionDate"
              control={control}
              render={({ field }) => (
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Expected Date of Contracting"
                    value={field.value}
                    onChange={(newValue: Date | null) => {
                      const adjustedDate = newValue
                        ? new Date(newValue.setHours(12, 0, 0, 0))
                        : null;
                      field.onChange(adjustedDate);
                    }}
                    format="dd/MM/yyyy"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.expectedContractionDate,
                        helperText: errors.expectedContractionDate?.message,
                      },
                    }}
                    disabled={!!sale?.dateOfContraction}
                  />
                </LocalizationProvider>
              )}
            />
          </Grid>
        )}

        {isSameStatus(statusName, SalesStatuses.CONGRATS_ITS_A_SALE) && (
          <Grid item mobile={12} desktop={6}>
            <Controller
              name="dateOfContraction"
              control={control}
              render={({ field }) => (
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Date of Contraction"
                    format="dd/MM/yyyy"
                    value={
                      field.value || new Date(new Date().setHours(12, 0, 0, 0))
                    }
                    onChange={(newValue: Date | null) => {
                      const adjustedDate = newValue
                        ? new Date(newValue.setHours(12, 0, 0, 0))
                        : null;
                      field.onChange(adjustedDate);
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.dateOfContraction,
                        helperText: errors.dateOfContraction?.message,
                      },
                    }}
                    disabled
                  />
                </LocalizationProvider>
              )}
            />
          </Grid>
        )}

        <Grid item mobile={12} desktop={6}>
          <Controller
            name="saleCategory"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.saleCategory}>
                <InputLabel id="sale-category-label">Sale Category</InputLabel>
                <Select
                  {...field}
                  labelId="sale-category-label"
                  id="sale-category"
                  label="Sale Category"
                  value={field.value || sale?.saleCategory || ""}
                >
                  {sale_categories.map((category) => (
                    <MenuItem key={category.value} value={category.value}>
                      {category.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.saleCategory && (
                  <FormHelperText>{errors.saleCategory.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Grid>

        <Grid item mobile={12} desktop={6}>
          <Controller
            name="nameOnContract"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Name on Contract"
                fullWidth
                error={!!errors.nameOnContract}
                helperText={errors.nameOnContract?.message}
              />
            )}
          />
        </Grid>
      </Grid>

      <Grid item mobile={12}>
        <Controller
          name="comment"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              margin="normal"
              label="Comment"
              InputLabelProps={{
                shrink: field.value ? true : false,
              }}
              multiline
              rows={4}
              error={!!errors.comment}
              helperText={errors.comment?.message}
              inputProps={{ minLength: 4 }}
            />
          )}
        />
      </Grid>

      <Grid
        item
        mobile={12}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Typography variant="h4_med">Upload Documents *</Typography>
        <FileUploadButton
          setFiles={handleFiles}
          acceptedFileTypes={["image/jpeg", "image/png", "application/pdf"]}
          onDeleteFile={handleDeleteFile}
        />
      </Grid>

      <FormFooter
        handleClose={handleClose}
        isLoadingSubmition={isLoadingSubmition}
        confirmText={sale ? "Update" : "Save"}
        disabled={
          isSameStatus(statusName, SalesStatuses.CONGRATS_ITS_A_SALE) &&
          files?.length === 0
        }
      />
    </form>
  );
};

export default PromiseSaleForm;
